import React, { useState, useEffect } from 'react';
import { ShoppingBag, Star } from 'lucide-react';

// =======================================
// AMAZON STYLE PRODUCT CARD
// =======================================
const AmazonProductCard = ({
  product,
  onAddToBag,
}) => {
  const [qty, setQty] = useState(1);

  const getPrice = () => {
    if (!product.tiers || product.tiers.length === 0) {
      return product.basePrice;
    }

    const tier = product.tiers.find(
      (t) => Number(t.qty) === Number(qty)
    );

    return tier
      ? Number(tier.price)
      : product.basePrice;
  };

  const finalPrice = getPrice();

  const originalPrice = Math.floor(
    finalPrice + finalPrice * 0.35
  );

  const handleAdd = () => {
    onAddToBag({
      id: product._id,
      name: product.title,
      image: product.image,
      qty,
      price: finalPrice,
      specs: product.description,
    });
  };

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 group">
      
      {/* IMAGE */}
      <div className="relative bg-[#f7f7f7] aspect-square overflow-hidden">
        
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-full object-contain p-3 sm:p-6 group-hover:scale-105 transition duration-500"
        />

        {/* PRIME BADGE */}
        <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 bg-white px-2 py-1 rounded-full shadow-sm border border-gray-100 flex items-center gap-1">
          
          <span className="text-[#ff9900] font-black text-[10px] sm:text-xs">
            ✔
          </span>

          <span className="text-[#146eb4] text-[10px] sm:text-xs font-bold">
            Verified By Daily Go
          </span>
        </div>
      </div>

      {/* CONTENT */}
      <div className="p-3 sm:p-4 flex flex-col">

        {/* TITLE */}
        <h3 className="text-[14px] sm:text-[20px] font-medium text-black leading-snug line-clamp-2 min-h-[42px] sm:min-h-[60px] hover:text-[#c7511f] cursor-pointer transition">
          {product.title}
        </h3>

        {/* RATING */}
        <div className="flex items-center gap-0.5 sm:gap-1 mt-2">
          
          {[1, 2, 3, 4].map((i) => (
            <Star
              key={i}
              size={12}
              className="fill-[#ffa41c] text-[#ffa41c] sm:w-[15px] sm:h-[15px]"
            />
          ))}

          <Star
            size={12}
            className="text-[#ffa41c] sm:w-[15px] sm:h-[15px]"
          />

          <span className="text-[#007185] text-[11px] sm:text-sm ml-1">
            1,248
          </span>
        </div>

        {/* PRICE */}
        <div className="mt-2 sm:mt-3">
          
          <div className="flex items-end gap-1 sm:gap-2 flex-wrap">
            
            <span className="text-[24px] sm:text-[34px] font-medium text-black leading-none">
              ₹{finalPrice}
            </span>

            <span className="text-gray-500 text-[12px] sm:text-[16px] line-through">
              ₹{originalPrice}
            </span>
          </div>

          <p className="text-[11px] sm:text-sm text-green-700 mt-1 font-medium">
            FREE Delivery
          </p>
        </div>

        {/* SELECT */}
        <div className="mt-3 sm:mt-4">
          
          <select
            value={qty}
            onChange={(e) =>
              setQty(Number(e.target.value))
            }
            className="w-full bg-[#f0f2f2] border border-gray-300 rounded-lg px-3 py-2 text-[12px] sm:text-sm outline-none hover:bg-gray-200 transition"
          >
            {product.tiers?.map((tier) => (
              <option
                key={tier.qty}
                value={tier.qty}
              >
                Qty: {tier.qty}
              </option>
            )) || (
              <>
                {[1, 2, 3, 4, 5].map((q) => (
                  <option key={q} value={q}>
                    Qty: {q}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>

        {/* BUTTON */}
        <button
          onClick={handleAdd}
          className="mt-3 sm:mt-5 bg-[#ffd814] hover:bg-[#f7ca00] text-black font-medium py-2.5 sm:py-3 rounded-full transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.98] text-[13px] sm:text-base"
        >
          <ShoppingBag size={16} className="sm:w-[18px] sm:h-[18px]" />
          Add to Bag
        </button>
      </div>
    </div>
  );
};

// =======================================
// MAIN SECTION
// =======================================
const AmazonProductsSection = ({
  onAddToCart,
}) => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  // FETCH PRODUCTS
  const fetchProducts = async () => {
    try {
      const res = await fetch(
        'https://dgl-core-9x7.dailygolive.in/api/products'
      );

      const data = await res.json();

      const formatted = data.map((item) => ({
        ...item,
        title: item.name,
        basePrice: Number(item.basePrice || 0),
        tiers: (item.tiers || []).map((t) => ({
          qty: Number(t.qty),
          price: Number(t.price),
        })),
      }));

      setProducts(formatted);

    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="w-full bg-[#eaeded] py-5 sm:py-8">
      
      {/* HEADER */}
      <div className="px-3 sm:px-6 lg:px-8 mb-5 sm:mb-8">
        
        <div className="flex flex-col gap-2">
          
          <h2 className="text-2xl sm:text-4xl font-bold text-black">
            Recommended Products
          </h2>

          <p className="text-gray-600 text-[13px] sm:text-base max-w-2xl leading-relaxed ml-20">
            Discover premium products with fast delivery,
            verified quality, and bulk ordering support.
          </p>
        </div>
      </div>

      {/* PRODUCTS GRID */}
      <div className="px-2 sm:px-6 lg:px-8">
        
        {/* MOBILE AMAZON STYLE */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-6">
          
          {products.map((product) => (
            <AmazonProductCard
              key={product._id}
              product={product}
              onAddToBag={onAddToCart}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AmazonProductsSection;